import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_INFO = { phone: '919-346-4803', hours_weekday: 'Mon-Fri: 9:00 AM - 6:00 PM', hours_weekend: 'Sat: 10:00 AM - 4:00 PM', locations: 'Henderson, NC\nDurham, NC', location_note: 'We also offer on-site training at your location' };

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(DEFAULT_INFO);

  useEffect(() => {
    axios.get(`${API}/content/contact_info`).then(r => setInfo({ ...DEFAULT_INFO, ...r.data })).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get in <span className="text-red-600">Touch</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Have questions about our training programs? Ready to book a class? We're here to help.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription className="text-base">
                    Fill out the form below and we'll respond within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Your Email *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(999) 999-9999" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your training needs..." rows={6} required />
                    </div>
                    <Button type="submit" size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={submitting}>
                      <Send className="mr-2" size={20} />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-2 border-red-100">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                    <Phone className="text-red-600" size={24} />
                  </div>
                  <CardTitle>Phone</CardTitle>
                </CardHeader>
                <CardContent>
                  <a href={`tel:${info.phone}`} className="text-lg text-red-600 hover:text-red-700 font-medium">
                    {info.phone}
                  </a>
                  <p className="text-sm text-gray-600 mt-2">
                    {info.hours_weekday}<br />{info.hours_weekend}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-100">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                    <Mail className="text-red-600" size={24} />
                  </div>
                  <CardTitle>Email</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">Use the contact form to reach us and we'll respond within 24 hours.</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-100">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                    <MapPin className="text-red-600" size={24} />
                  </div>
                  <CardTitle>Locations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-medium text-gray-900 whitespace-pre-line">{info.locations}</p>
                  <p className="text-sm text-gray-600 mt-2">{info.location_note}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl">Service Areas</CardTitle>
              <CardDescription className="text-lg">
                Proudly serving Henderson, Durham, and surrounding North Carolina communities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-gray-600 text-lg">
                Can't find a class in your area? Contact us about bringing our training to your location!
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
